import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import styled from 'styled-components';
import { createPlainTextPasteHandler } from '../../../utils/clipboardPaste';

const isEditorEmpty = (html = '') => {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length === 0;
};

// Register custom icons for toolbar buttons
const icons = Quill.import('ui/icons');
icons['undo'] = '<svg viewBox="0 0 18 18"><polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon><path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path></svg>';
icons['redo'] = '<svg viewBox="0 0 18 18"><polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon><path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path></svg>';

const svgIcon = (path) =>
  `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path fill="currentColor" d="${path}"/></svg>`;

const RESPONSE_ICONS = {
  bold: svgIcon(
    'M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z',
  ),
  italic: svgIcon('M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z'),
  underline: svgIcon(
    'M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z',
  ),
  header: svgIcon('M5 4v3h5.5v12h3V7H19V4z'),
  size: svgIcon('M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z'),
  align: {
    '': svgIcon('M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z'),
    center: svgIcon(
      'M7 15v2h10v-2H7zm-4-8v2h18V7H3zm4 6h10v-2H7v2zm-4 6h18v-2H3v2zM3 3v2h18V3H3z',
    ),
    right: svgIcon('M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z'),
    justify: svgIcon(
      'M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z',
    ),
  },
};

const applyResponseToolbarIcons = (toolbar) => {
  const setIcon = (selector, html) => {
    const el = toolbar.querySelector(selector);
    if (el) el.innerHTML = html;
  };
  setIcon('button.ql-bold', RESPONSE_ICONS.bold);
  setIcon('button.ql-italic', RESPONSE_ICONS.italic);
  setIcon('button.ql-underline', RESPONSE_ICONS.underline);
  setIcon('.ql-header .ql-picker-label', RESPONSE_ICONS.header);
  setIcon('.ql-size .ql-picker-label', RESPONSE_ICONS.size);
  toolbar.querySelectorAll('.ql-align .ql-picker-item').forEach((item) => {
    const value = item.getAttribute('data-value') || '';
    item.innerHTML = RESPONSE_ICONS.align[value] || RESPONSE_ICONS.align[''];
  });
  const alignLabel = toolbar.querySelector('.ql-align .ql-picker-label');
  if (alignLabel) {
    const value = alignLabel.getAttribute('data-value') || '';
    alignLabel.innerHTML = RESPONSE_ICONS.align[value] || RESPONSE_ICONS.align[''];
  }
};

const QuestionRichTextEditor = ({
  value,
  plainText = false,
  onChange,
  placeholder,
  disabled = false,
  hasFooter = false,
  toolbarVariant = 'default',
}) => {
  const toolbarRef = useRef(null);
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const lastHtmlRef = useRef('');
  const lastSelectionRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const pendingEmittedValueRef = useRef(null);
  const isInitializingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(() => isEditorEmpty(value));

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setIsEmpty(isEditorEmpty(value));
  }, [value]);

  useEffect(() => {
    const mount = editorRef.current;
    const toolbar = toolbarRef.current;

    // Prevent initialization if already in progress or completed
    if (!mount || !toolbar || isInitializingRef.current || isInitializedRef.current || quillRef.current) {
      return undefined;
    }

    // Double-check DOM for existing Quill elements
    if (mount.classList.contains('ql-container') || toolbar.classList.contains('ql-toolbar')) {
      isInitializedRef.current = true;
      return undefined;
    }

    // Mark as initializing to prevent concurrent initialization
    isInitializingRef.current = true;

    const quill = new Quill(mount, {
      modules: {
        toolbar: { container: toolbar },
        history: {
          delay: 1000,
          maxStack: 50,
          userOnly: true
        }
      },
      theme: 'snow',
      placeholder: toolbarVariant === 'response' ? '' : (placeholder || ''),
      readOnly: disabled,
    });

    const Delta = Quill.import('delta');
    quill.setContents(quill.clipboard.convert(plainText ? { text: value || '' } : { html: value || '' }), 'silent');
    lastHtmlRef.current = quill.root.innerHTML;
    quillRef.current = quill;
    isInitializedRef.current = true;
    isInitializingRef.current = false;
    setIsEmpty(quill.getText().trim().length === 0);

    const handleSelectionChange = (range) => {
      if (range) lastSelectionRef.current = range;
    };

    const handleTextChange = (_delta, _oldDelta, source) => {
      setIsEmpty(quill.getText().trim().length === 0);
      if (source !== 'user') return;
      const html = quill.root.innerHTML;
      lastHtmlRef.current = html;
      pendingEmittedValueRef.current = html;
      lastSelectionRef.current = quill.getSelection() || lastSelectionRef.current;
      onChangeRef.current?.(html);
    };

    quill.on('selection-change', handleSelectionChange);
    quill.on('text-change', handleTextChange);

    // Undo/Redo button handlers
    const undoButton = toolbar.querySelector('.ql-undo');
    const redoButton = toolbar.querySelector('.ql-redo');
    if (undoButton) {
      undoButton.addEventListener('click', () => {
        quill.history.undo();
      });
    }
    if (redoButton) {
      redoButton.addEventListener('click', () => {
        quill.history.redo();
      });
    }

    const handlePaste = createPlainTextPasteHandler(quill);

    quill.clipboard.addMatcher('img', () => new Delta());
    quill.root.addEventListener('paste', handlePaste, true);

    if (toolbarVariant === 'response') {
      applyResponseToolbarIcons(toolbar);
      quill.root.style.textAlign = 'left';
      quill.root.classList.remove('ql-align-center', 'ql-align-right', 'ql-align-justify');
    }

    const toolbarContainer = quill.getModule('toolbar')?.container;
    const handleToolbarMouseDown = (e) => {
      if (!e.target.closest('button, select, .ql-picker-label, .ql-picker-item')) return;
      e.preventDefault();
    };

    if (toolbarContainer) {
      toolbarContainer.addEventListener('mousedown', handleToolbarMouseDown);
    }

    return () => {
      if (!quillRef.current) return;

      quill.off('selection-change', handleSelectionChange);
      quill.off('text-change', handleTextChange);
      if (quill.root) {
        quill.root.removeEventListener('paste', handlePaste, true);
      }
      if (toolbarContainer) {
        toolbarContainer.removeEventListener('mousedown', handleToolbarMouseDown);
      }

      // Clean up refs
      quillRef.current = null;
      isInitializedRef.current = false;
      isInitializingRef.current = false;

      if (mount) {
        mount.innerHTML = '';
        mount.classList.remove('ql-container', 'ql-snow');
      }
      if (toolbar) {
        toolbar.classList.remove('ql-toolbar', 'ql-snow');
      }
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.enable(!disabled);

    const nextValue = value || '';
    if (pendingEmittedValueRef.current === nextValue) {
      pendingEmittedValueRef.current = null;
      lastHtmlRef.current = nextValue;
      setIsEmpty(quill.getText().trim().length === 0);
      return;
    }
    if (nextValue === lastHtmlRef.current || nextValue === quill.root.innerHTML) {
      lastHtmlRef.current = nextValue;
      setIsEmpty(quill.getText().trim().length === 0);
      return;
    }
    if (quill.hasFocus()) return;

    const selection = quill.getSelection() || lastSelectionRef.current;
    quill.setContents(quill.clipboard.convert(plainText ? { text: nextValue } : { html: nextValue }), 'silent');
    lastHtmlRef.current = quill.root.innerHTML;
    setIsEmpty(quill.getText().trim().length === 0);
    if (selection) {
      const maxIndex = Math.max(0, quill.getLength() - 1);
      quill.setSelection(Math.min(selection.index, maxIndex), selection.length);
    }
  }, [value, disabled, plainText]);

  return (
    <EditorShell
      className={`question-quill-shell${toolbarVariant === 'response' ? ' response-editor' : ''}`}
      $hasFooter={hasFooter}
      $toolbarVariant={toolbarVariant}
    >
      <ToolbarMount ref={toolbarRef}>
        {toolbarVariant === 'response' ? (
          <span className="ql-formats">
            <button type="button" className="ql-bold" aria-label="Bold" />
            <button type="button" className="ql-italic" aria-label="Italic" />
            <button type="button" className="ql-underline" aria-label="Underline" />
            <select className="ql-header" aria-label="Heading" />
            <select className="ql-size" aria-label="Font size" />
            <select className="ql-align" aria-label="Align" />
          </span>
        ) : (
          <>
            <span className="ql-formats">
              <button type="button" className="ql-undo" />
              <button type="button" className="ql-redo" />
            </span>
            <span className="ql-formats">
              <button type="button" className="ql-bold" />
              <button type="button" className="ql-italic" />
              <button type="button" className="ql-underline" />
              <button type="button" className="ql-strike" />
            </span>
            <span className="ql-formats">
              <button type="button" className="ql-script" value="sub" />
              <button type="button" className="ql-script" value="super" />
            </span>
          </>
        )}
      </ToolbarMount>
      <EditorArea>
        <EditorMount ref={editorRef} className="question-quill-mount" />
        {toolbarVariant === 'response' && isEmpty && placeholder ? (
          <ResponsePlaceholder aria-hidden="true">{placeholder}</ResponsePlaceholder>
        ) : null}
      </EditorArea>
    </EditorShell>
  );
};

const editorChromeStyles = `
  .ql-toolbar.ql-snow {
    border: none !important;
    border-bottom: 1px solid #e5e5e5 !important;
    border-radius: 0 !important;
    background: #fff;
    padding: 8px 16px !important;
    font-family: var(--font-family-ibm_plex_sans);
    position: static;
    top: auto;
    margin: 0 !important;
    z-index: auto;
    box-sizing: border-box;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px;
    height: auto !important;
    min-height: 44px !important;
    float: none !important;
  }

  .ql-toolbar.ql-snow::before,
  .ql-toolbar.ql-snow::after {
    display: none !important;
    content: none !important;
  }

  .ql-container.ql-snow {
    border: none !important;
    border-radius: 0 !important;
    font-family: var(--font-family-ibm_plex_sans);
    font-size: 14px;
    overflow: hidden;
    box-sizing: border-box;
  }
`;

const EditorShell = styled.div`
  width: 100%;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: ${(props) => (props.$hasFooter ? '8px 8px 0 0' : '8px')};
  border-bottom: ${(props) => (props.$hasFooter ? 'none' : '1px solid #d1d5db')};
  overflow: hidden;
  transition: border-color 0.2s;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &:focus-within {
    border-color: #9ca3af;
  }

  ${editorChromeStyles}

  .ql-toolbar.ql-snow .ql-formats {
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
    float: none !important;
  }

  .ql-toolbar.ql-snow .ql-formats::after {
    display: none !important;
    content: none !important;
  }

  .ql-snow.ql-toolbar button,
  .ql-snow .ql-toolbar button,
  .ql-toolbar.ql-snow button {
    float: none !important;
    width: 28px !important;
    height: 28px !important;
    padding: 0 !important;
    margin: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    background: transparent !important;
    cursor: pointer !important;
    border-radius: 4px !important;
    transition: background-color 0.2s !important;
    flex-shrink: 0 !important;
    box-sizing: border-box !important;
  }

  .ql-toolbar.ql-snow button:hover {
    background-color: #f5f5f5 !important;
  }

  .ql-snow.ql-toolbar button svg,
  .ql-snow .ql-toolbar button svg,
  .ql-toolbar.ql-snow button svg {
    float: none !important;
    width: 16px !important;
    height: 16px !important;
    display: block !important;
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .ql-toolbar.ql-snow button[class^='ql-']::before,
  .ql-toolbar.ql-snow button[class^='ql-']::after {
    display: none !important;
    content: none !important;
  }

  /* Ensure all toolbar button icons are visible */
  .ql-toolbar.ql-snow .ql-stroke {
    fill: none;
    stroke: #333;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2;
  }

  .ql-toolbar.ql-snow .ql-fill {
    fill: #333;
  }

  .ql-toolbar.ql-snow .ql-even {
    fill-rule: evenodd;
  }

  .ql-toolbar.ql-snow .ql-thin {
    stroke-width: 1.5;
  }

  .ql-toolbar.ql-snow .ql-transparent {
    opacity: 0.4;
  }

  /* Active button state */
  .ql-toolbar.ql-snow button.ql-active {
    background-color: #e8f0fe;
  }

  .ql-toolbar.ql-snow button.ql-active .ql-stroke {
    stroke: #1a73e8;
  }

  .ql-toolbar.ql-snow button.ql-active .ql-fill {
    fill: #1a73e8;
  }

  .ql-container .ql-editor {
    min-height: 100px;
    height: auto;
    overflow-y: visible;
    padding: 16px 12px !important;
    line-height: 1.5;
    color: #1a1a1a;
    border: none;
    border-radius: 0;
  }

  .ql-container .ql-editor strong {
    font-weight: 700;
  }

  .ql-container .ql-editor em {
    font-style: italic;
  }

  .ql-container .ql-editor.ql-blank::before {
    color: #9ca3af;
    font-style: italic;
    left: 15px !important;
    right: auto !important;
    text-align: left !important;
    width: auto;
    max-width: calc(100% - 30px);
    padding: 0 !important;
  }

  &.response-editor .ql-container .ql-editor.ql-blank::before {
    display: none !important;
    content: none !important;
  }

  ${(props) =>
    props.$toolbarVariant === 'response'
      ? `
  .ql-toolbar.ql-snow {
    justify-content: center;
    background: #fafafa;
    padding: 8px 16px;
    overflow: visible;
    z-index: 2;
    border-radius: 7px 7px 0 0 !important;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 7px 7px !important;
  }

  .ql-container .ql-editor {
    text-align: left !important;
  }

  .ql-toolbar.ql-snow .ql-picker:hover .ql-picker-label,
  .ql-toolbar.ql-snow .ql-picker-label:hover {
    background-color: #f5f5f5;
    border-radius: 4px;
  }

  .ql-toolbar.ql-snow .ql-formats {
    gap: 8px;
  }

  .ql-toolbar.ql-snow button,
  .ql-toolbar.ql-snow .ql-picker {
    float: none;
    width: 32px;
    height: 32px;
    color: #4b464f;
    margin: 0;
  }

  .ql-toolbar.ql-snow button svg,
  .ql-toolbar.ql-snow .ql-picker-label svg {
    width: 22px;
    height: 22px;
    display: block;
    float: none;
    position: static;
    margin: 0;
    right: auto;
  }

  .ql-toolbar.ql-snow .ql-picker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ql-toolbar.ql-snow .ql-picker-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-header .ql-picker-label::before,
  .ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-label::before {
    display: none;
    content: none;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-header,
  .ql-toolbar.ql-snow .ql-picker.ql-size {
    width: 32px;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-align {
    width: 40px;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-align .ql-picker-label {
    padding-right: 8px;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-align .ql-picker-label::after {
    content: '';
    width: 0;
    height: 0;
    margin-left: 2px;
    border-left: 3.5px solid transparent;
    border-right: 3.5px solid transparent;
    border-top: 4px solid #4b464f;
  }

  .ql-toolbar.ql-snow .ql-picker-options {
    min-width: 120px;
    padding: 8px;
    background: #fff;
    border: 1px solid #e5e5e5;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 10;
  }

  .ql-toolbar.ql-snow .ql-icon-picker .ql-picker-item {
    width: 24px;
    height: 24px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ql-toolbar.ql-snow .ql-icon-picker .ql-picker-item svg {
    width: 20px;
    height: 20px;
  }

  .ql-toolbar.ql-snow button.ql-active,
  .ql-toolbar.ql-snow .ql-picker-label.ql-active {
    color: #1a73e8;
    background-color: #e8f0fe;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-align .ql-picker-label.ql-active::after {
    border-top-color: #1a73e8;
  }
  `
      : ''}
`;

const ToolbarMount = styled.div``;

const EditorArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ResponsePlaceholder = styled.div`
  position: absolute;
  top: 16px;
  left: 12px;
  right: auto;
  max-width: calc(100% - 24px);
  color: #9ca3af;
  font-style: italic;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 1.5;
  pointer-events: none;
  text-align: left;
  z-index: 1;
`;

const EditorMount = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
`;

export default QuestionRichTextEditor;
